import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Table, Button, Input, Avatar, Popconfirm,
  Form, Select, Space, Tooltip, Breadcrumb,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, UserOutlined,
  SaveOutlined, PhoneOutlined, MailOutlined,
  TeamOutlined, WarningOutlined, CheckCircleOutlined,
  FileTextOutlined, HomeOutlined,
} from "@ant-design/icons";

/* ── design tokens ─────────────────────────────────────────────────────── */
const T = {
  bg:      "#f1f5f9",
  card:    "#ffffff",
  border:  "#e2e8f0",
  borderL: "#f1f5f9",
  primary: "#2563eb",
  text:    "#0f172a",
  sub:     "#64748b",
  muted:   "#94a3b8",
  red:     "#e11d48",
  green:   "#16a34a",
};

const card = {
  background: T.card,
  borderRadius: 14,
  border: `1px solid ${T.border}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  overflow: "hidden",
};

/* ── fake data ─────────────────────────────────────────────────────────── */
const FAKE = [
  { id: 1, name: "Ahmed Al-Rashid",  phone: "0750 111 2233", type: "Individual", balance: 450000, orders: 12 },
  { id: 2, name: "Velan Co.",         phone: "0770 555 8844", type: "Company",    balance: 0,      orders: 38 },
  { id: 3, name: "Sara Hassan",       phone: "0780 323 9900", type: "Individual", balance: 120000, orders: 5  },
  { id: 4, name: "Al-Noor Trading",   phone: "0751 667 4412", type: "Company",    balance: 870000, orders: 21 },
  { id: 5, name: "Karwan Omer",       phone: "0773 440 2211", type: "Individual", balance: 0,      orders: 7  },
];

/* ── Stat card ─────────────────────────────────────────────────────────── */
function Stat({ icon, label, value, iconBg, valueColor = T.text }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 160, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 11, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 21, fontWeight: 800, color: valueColor, lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 12, color: T.muted, fontWeight: 500, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

/* ── Page header (shared) ──────────────────────────────────────────────── */
function PageHeader({ breadcrumb, title, subtitle, action }) {
  return (
    <div style={{ marginBottom: 20 }}>
      {breadcrumb && (
        <Breadcrumb
          style={{ marginBottom: 8, fontSize: 13 }}
          items={breadcrumb}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>{title}</h1>
          {subtitle && <p style={{ margin: "3px 0 0", fontSize: 13, color: T.sub }}>{subtitle}</p>}
        </div>
        {action}
      </div>
    </div>
  );
}

/* ── Section header inside form card ──────────────────────────────────── */
function Section({ title, sub }) {
  return (
    <div style={{ marginBottom: 18, paddingBottom: 12, borderBottom: `1px solid ${T.borderL}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   LIST PAGE
───────────────────────────────────────────────────────────────────────── */
function ListView({ onAdd, onEdit }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const rows = FAKE.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()) || r.phone.includes(search)
  );
  const totalOwed = rows.reduce((s, r) => s + r.balance, 0);
  const withDebt  = rows.filter((r) => r.balance > 0).length;
  const fmt = (n) => t("demo.ui.currency.iqdAmount", {
    defaultValue: "{{amount}} IQD",
    amount: n.toLocaleString(),
  });
  const customerType = (type) => t(`demo.ui.customerType.${type.toLowerCase()}`, {
    defaultValue: type,
  });

  const columns = [
    { title: "#", key: "n", width: 48, render: (_, __, i) => <span style={{ color: T.muted, fontWeight: 700, fontSize: 13 }}>{i + 1}</span> },
    {
      title: t("demo.ui.table.customer", { defaultValue: "Customer" }), key: "cust",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar size={36} style={{ background: r.type === "Company" ? "#eff6ff" : "#fdf4ff", color: r.type === "Company" ? T.primary : "#7c3aed", fontWeight: 800, fontSize: 14, border: `2px solid ${r.type === "Company" ? "#bfdbfe" : "#e9d5ff"}` }}>
            {r.name[0]}
          </Avatar>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>{r.name}</div>
            <div style={{ fontSize: 12, color: T.muted }}>{r.phone}</div>
          </div>
        </div>
      ),
    },
    {
      title: t("demo.ui.table.type", { defaultValue: "Type" }), dataIndex: "type", key: "type",
      render: (t) => (
        <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: t === "Company" ? "#eff6ff" : "#fdf4ff", color: t === "Company" ? T.primary : "#7c3aed", border: `1px solid ${t === "Company" ? "#bfdbfe" : "#e9d5ff"}` }}>
          {customerType(t)}
        </span>
      ),
    },
    {
      title: t("demo.ui.table.orders", { defaultValue: "Orders" }), dataIndex: "orders", key: "orders", align: "center",
      render: (v) => <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: "#f1f5f9", color: "#475569" }}><FileTextOutlined style={{ fontSize: 11, marginRight: 4 }} />{v}</span>,
    },
    {
      title: t("demo.ui.table.balance", { defaultValue: "Balance" }), dataIndex: "balance", key: "balance", align: "right",
      render: (v) => v > 0
        ? <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: "#fff1f2", color: T.red, border: "1px solid #fecdd3" }}><WarningOutlined style={{ fontSize: 10, marginRight: 4 }} />{fmt(v)}</span>
        : <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: "#f0fdf4", color: T.green, border: "1px solid #bbf7d0" }}><CheckCircleOutlined style={{ fontSize: 10, marginRight: 4 }} />{t("demo.ui.status.paid", { defaultValue: "Paid" })}</span>,
    },
    {
      title: "", key: "act", width: 72,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          <Tooltip title={t("demo.ui.actions.edit", { defaultValue: "Edit" })}><Button aria-label={t("demo.ui.actions.editCustomer", { defaultValue: "Edit customer" })} size="small" icon={<EditOutlined />} onClick={() => onEdit(r)} style={{ borderRadius: 7, width: 30, height: 30, padding: 0 }} /></Tooltip>
          <Tooltip title={t("demo.ui.actions.delete", { defaultValue: "Delete" })}>
            <Popconfirm title={t("demo.ui.confirm.deleteCustomer", { defaultValue: "Delete this customer?" })} okText={t("demo.ui.actions.delete", { defaultValue: "Delete" })} okButtonProps={{ danger: true }} cancelText={t("demo.ui.actions.cancel", { defaultValue: "Cancel" })}>
              <Button aria-label={t("demo.ui.actions.deleteCustomer", { defaultValue: "Delete customer" })} size="small" icon={<DeleteOutlined />} danger style={{ borderRadius: 7, width: 30, height: 30, padding: 0 }} />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumb={[{ title: <HomeOutlined /> }, { title: t("demo.ui.customers.title", { defaultValue: "Customers" }) }]}
        title={t("demo.ui.customers.title", { defaultValue: "Customers" })}
        subtitle={t("demo.ui.customers.subtitle", { defaultValue: "Manage your customer list and track balances" })}
        action={
          <Button type="primary" icon={<PlusOutlined />} onClick={onAdd} style={{ borderRadius: 9, fontWeight: 700, height: 38, paddingInline: 18 }}>
            {t("demo.ui.actions.addCustomer", { defaultValue: "Add Customer" })}
          </Button>
        }
      />

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <Stat icon={<TeamOutlined style={{ color: T.primary }} />} label={t("demo.ui.stats.totalCustomers", { defaultValue: "Total Customers" })} value={rows.length} iconBg="#eff6ff" />
        <Stat icon={<WarningOutlined style={{ color: T.red }} />} label={t("demo.ui.stats.withDebt", { defaultValue: "With Debt" })} value={withDebt} iconBg="#fff1f2" valueColor={T.red} />
        <Stat icon={<span style={{ color: T.green, fontWeight: 800, fontSize: 15 }}>{t("demo.ui.currency.iqd", { defaultValue: "IQD" })}</span>} label={t("demo.ui.stats.totalOwed", { defaultValue: "Total Owed" })} value={fmt(totalOwed)} iconBg="#f0fdf4" valueColor={T.red} />
      </div>

      {/* Table card */}
      <div style={card}>
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${T.borderL}`, background: "#fafafa" }}>
          <Input prefix={<SearchOutlined style={{ color: T.muted }} />} placeholder={t("demo.ui.search.nameOrPhone", { defaultValue: "Search by name or phone…" })} value={search} onChange={(e) => setSearch(e.target.value)} allowClear style={{ maxWidth: 300, borderRadius: 8 }} />
        </div>
        <Table
          dataSource={rows.map((r) => ({ ...r, key: r.id }))}
          columns={columns}
          pagination={{ pageSize: 10, size: "small", style: { padding: "10px 20px 4px" } }}
          size="middle"
          onRow={() => ({ style: { transition: "background 0.1s" }, onMouseEnter: (e) => { e.currentTarget.style.background = "#fafafa"; }, onMouseLeave: (e) => { e.currentTarget.style.background = ""; } })}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   ADD / EDIT PAGE
───────────────────────────────────────────────────────────────────────── */
function FormView({ record, onBack }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEdit = Boolean(record);

  const inputStyle = { borderRadius: 9, height: 42 };
  const labelStyle = { fontWeight: 600, fontSize: 13, color: T.text };

  return (
    <div>
      {/* Page header */}
      <PageHeader
        breadcrumb={[{ title: <HomeOutlined /> }, { title: <span style={{ cursor: "pointer" }} onClick={onBack}>{t("demo.ui.customers.title", { defaultValue: "Customers" })}</span> }, { title: isEdit ? t("demo.ui.actions.edit", { defaultValue: "Edit" }) : t("demo.ui.actions.add", { defaultValue: "Add" }) }]}
        title={isEdit ? t("demo.ui.form.editTitle", { defaultValue: "Edit Customer" }) : t("demo.ui.form.addTitle", { defaultValue: "Add Customer" })}
        subtitle={isEdit ? t("demo.ui.form.editSubtitle", { defaultValue: "Editing details for {{name}}", name: record.name }) : t("demo.ui.form.addSubtitle", { defaultValue: "Fill in the details to add a new customer" })}
        action={
          <Space>
            <Button onClick={onBack} style={{ borderRadius: 9, height: 38 }}>{t("demo.ui.actions.cancel", { defaultValue: "Cancel" })}</Button>
            <Button type="primary" icon={<SaveOutlined />} style={{ borderRadius: 9, fontWeight: 700, height: 38, paddingInline: 20 }}>{t("demo.ui.actions.saveCustomer", { defaultValue: "Save Customer" })}</Button>
          </Space>
        }
      />

      {/* Two-column layout: form + info panel */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16, alignItems: "start" }}>

        {/* Left — form */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Card 1: Basic info */}
          <div style={card}>
            <div style={{ padding: "16px 22px" }}>
              <Section title={t("demo.ui.form.basicInformation", { defaultValue: "Basic Information" })} sub={t("demo.ui.form.basicInformationHint", { defaultValue: "Name and account type" })} />
              <Form form={form} layout="vertical" initialValues={record || {}}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                  <Form.Item name="name" label={<span style={labelStyle}>{t("demo.ui.form.fullName", { defaultValue: "Full Name" })}</span>} rules={[{ required: true, message: t("demo.ui.validation.nameRequired", { defaultValue: "Name is required" }) }]}>
                    <Input prefix={<UserOutlined style={{ color: T.muted }} />} placeholder={t("demo.ui.form.fullNamePlaceholder", { defaultValue: "Enter full name" })} style={inputStyle} />
                  </Form.Item>
                  <Form.Item name="type" label={<span style={labelStyle}>{t("demo.ui.form.customerType", { defaultValue: "Customer Type" })}</span>}>
                    <Select placeholder={t("demo.ui.form.selectType", { defaultValue: "Select type" })} style={{ height: 42 }}>
                      <Select.Option value="Individual">{t("demo.ui.customerType.individual", { defaultValue: "Individual" })}</Select.Option>
                      <Select.Option value="Company">{t("demo.ui.customerType.company", { defaultValue: "Company" })}</Select.Option>
                      <Select.Option value="Organization">{t("demo.ui.customerType.organization", { defaultValue: "Organization" })}</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </Form>
            </div>
          </div>

          {/* Card 2: Contact */}
          <div style={card}>
            <div style={{ padding: "16px 22px" }}>
              <Section title={t("demo.ui.form.contactDetails", { defaultValue: "Contact Details" })} sub={t("demo.ui.form.contactDetailsHint", { defaultValue: "How to reach this customer" })} />
              <Form form={form} layout="vertical" initialValues={record || {}}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                  <Form.Item name="phone" label={<span style={labelStyle}>{t("demo.ui.form.phoneNumber", { defaultValue: "Phone Number" })}</span>}>
                    <Input prefix={<PhoneOutlined style={{ color: T.muted }} />} placeholder={t("demo.ui.form.phonePlaceholder", { defaultValue: "07xx xxx xxxx" })} style={inputStyle} />
                  </Form.Item>
                  <Form.Item name="email" label={<span style={labelStyle}>{t("demo.ui.form.emailAddress", { defaultValue: "Email Address" })}</span>}>
                    <Input prefix={<MailOutlined style={{ color: T.muted }} />} placeholder={t("demo.ui.form.emailPlaceholder", { defaultValue: "email@example.com" })} style={inputStyle} />
                  </Form.Item>
                </div>
                <Form.Item name="address" label={<span style={labelStyle}>{t("demo.ui.form.address", { defaultValue: "Address" })}</span>}>
                  <Input.TextArea placeholder={t("demo.ui.form.addressPlaceholder", { defaultValue: "Street, city, region…" })} rows={3} style={{ borderRadius: 9 }} />
                </Form.Item>
              </Form>
            </div>
          </div>

          {/* Bottom actions */}
          <div style={{ display: "flex", gap: 10 }}>
            <Button type="primary" icon={<SaveOutlined />} size="large" style={{ borderRadius: 10, fontWeight: 700, flex: 1, height: 46, background: T.primary }}>
              {isEdit ? t("demo.ui.actions.saveChanges", { defaultValue: "Save Changes" }) : t("demo.ui.actions.addCustomer", { defaultValue: "Add Customer" })}
            </Button>
            <Button size="large" onClick={onBack} style={{ borderRadius: 10, height: 46, width: 120 }}>
              {t("demo.ui.actions.cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </div>

        {/* Right — info panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Preview */}
          <div style={card}>
            <div style={{ padding: "16px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.muted, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("demo.ui.preview.title", { defaultValue: "Preview" })}</div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "12px 0" }}>
                <Avatar size={60} style={{ background: "#eff6ff", color: T.primary, fontWeight: 800, fontSize: 22, marginBottom: 12 }} icon={<UserOutlined />} />
                <div style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t("demo.ui.preview.newCustomer", { defaultValue: "New Customer" })}</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{t("demo.ui.customerType.individual", { defaultValue: "Individual" })}</div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div style={{ ...card, background: "#fffbeb", border: "1px solid #fde68a" }}>
            <div style={{ padding: "14px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("demo.ui.tips.title", { defaultValue: "💡 Tips" })}</div>
              {[
                t("demo.ui.tips.phoneLookup", { defaultValue: "Phone number is used for quick lookup in POS" }),
                t("demo.ui.tips.customerType", { defaultValue: "Set customer type for better reporting" }),
                t("demo.ui.tips.emailReceipts", { defaultValue: "Email is optional but useful for receipts" }),
              ].map((tip, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12, color: "#78350f" }}>
                  <span style={{ flexShrink: 0, marginTop: 1 }}>•</span>
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   MAIN
───────────────────────────────────────────────────────────────────────── */
export default function UIDemo() {
  const { t } = useTranslation();
  const [view, setView] = useState("list");
  const [editRec, setEditRec] = useState(null);

  return (
    <div style={{ padding: 24, background: T.bg, minHeight: "100%" }}>
      {/* Switcher */}
      <div style={{ display: "inline-flex", gap: 4, background: "#fff", padding: 5, borderRadius: 11, border: `1px solid ${T.border}`, marginBottom: 24 }}>
        {["list", "add", "edit"].map((v) => (
          <button
            key={v}
            onClick={() => { setView(v); if (v === "edit") setEditRec(FAKE[0]); }}
            style={{
              padding: "7px 18px", borderRadius: 7, border: "none", cursor: "pointer",
              fontWeight: 700, fontSize: 13,
              background: view === v ? T.primary : "transparent",
              color: view === v ? "#fff" : T.muted,
              transition: "all 0.15s",
            }}
          >
            {v === "list"
              ? t("demo.ui.switcher.listPage", { defaultValue: "List Page" })
              : v === "add"
                ? t("demo.ui.switcher.addPage", { defaultValue: "Add Page" })
                : t("demo.ui.switcher.editPage", { defaultValue: "Edit Page" })}
          </button>
        ))}
      </div>

      {view === "list" && <ListView onAdd={() => setView("add")} onEdit={(r) => { setEditRec(r); setView("edit"); }} />}
      {(view === "add" || view === "edit") && <FormView record={view === "edit" ? editRec : null} onBack={() => setView("list")} />}
    </div>
  );
}
